import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Thank you. We'll be in touch soon.");
      setForm({ name: "", email: "", company: "", message: "", website: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-base-bg/25 py-3 text-base-bg placeholder:text-base-bg/40 focus:border-sand focus:outline-none transition-colors";

  return (
    <footer id="contact" data-testid="contact-section" className="bg-emerald-950 text-base-bg pt-24 md:pt-36 pb-12">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10">
          {/* Invitation */}
          <div className="lg:col-span-5">
            <motion.p variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-[0.72rem] uppercase tracking-[0.22em] text-base-bg/50 mb-6">
              The invitation
            </motion.p>
            <motion.h2 custom={1} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Have an interesting <span className="italic text-sand">proposition?</span>
            </motion.h2>
            <motion.p custom={2} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-base-bg/70">
              We are always open to conversations with founders, innovators, and organizations that
              align with our values.
            </motion.p>

            <motion.div custom={3} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-12 space-y-2 text-base-bg/70">
              <p className="font-serif text-xl text-base-bg">TILT Ventures Ltd.</p>
              <p>Dublin, Ireland</p>
              <a data-testid="contact-email-link" href="mailto:investments@tiltventures.ie" className="inline-block border-b border-sand/50 text-sand hover:border-sand transition-colors">
                investments@tiltventures.ie
              </a>
            </motion.div>
          </div>

          {/* Form */}
          <div className="lg:col-span-6 lg:col-start-7">
            <motion.form
              data-testid="contact-form"
              onSubmit={submit}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8"
            >
              {/* Honeypot: hidden from real users; bots that fill it are silently discarded */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.website}
                onChange={update("website")}
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
              />
              <div>
                <label className="text-[0.7rem] uppercase tracking-[0.18em] text-base-bg/50">Name</label>
                <input data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Your name" className={field} />
              </div>
              <div>
                <label className="text-[0.7rem] uppercase tracking-[0.18em] text-base-bg/50">Email</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" className={field} />
              </div>
              <div className="md:col-span-2">
                <label className="text-[0.7rem] uppercase tracking-[0.18em] text-base-bg/50">Company / Organisation</label>
                <input data-testid="contact-company" value={form.company} onChange={update("company")} placeholder="Optional" className={field} />
              </div>
              <div className="md:col-span-2">
                <label className="text-[0.7rem] uppercase tracking-[0.18em] text-base-bg/50">Your proposition</label>
                <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={4} placeholder="Tell us what makes it interesting..." className={`${field} resize-none`} />
              </div>
              <div className="md:col-span-2">
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-3 bg-base-bg px-8 py-4 text-sm uppercase tracking-[0.18em] text-emerald-950 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send proposition"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </motion.form>
          </div>
        </div>

        <div className="mt-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-base-bg/15 pt-8 text-[0.72rem] uppercase tracking-[0.18em] text-base-bg/40">
          <span>© {new Date().getFullYear()} TILT Ventures Ltd.</span>
          <div className="flex items-center gap-6">
            <span className="hidden sm:inline">Rooted in Ireland · Reaching across borders</span>
            <Link data-testid="team-login-link" to="/login" className="inline-flex items-center gap-1.5 hover:text-sand transition-colors">
              <Lock className="h-3 w-3" /> Team
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
